package one.healthium.ui.compose.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material3.Icon
import androidx.compose.material3.SwipeToDismissBox
import androidx.compose.material3.SwipeToDismissBoxValue
import androidx.compose.material3.rememberSwipeToDismissBoxState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import one.healthium.ui.compose.theme.AppTheme

private val DeleteRed = Color(0xFFE53935)

/**
 * A swipe-to-delete container that reveals a red background with a trash icon
 * when swiped from end-to-start (right-to-left). When the swipe passes the threshold,
 * the content stays offset revealing the red background and a confirmation dialog is shown.
 *
 * @param modifier Modifier for the outer container.
 * @param shape Shape for the background. Defaults to [AppTheme.shapes.small].
 * @param confirmTitle Title for the confirmation dialog. If null, deletion happens immediately without confirmation.
 * @param confirmButton Text for the confirm button.
 * @param cancelButton Text for the cancel button.
 * @param onDelete Called when deletion is confirmed (or immediately if [confirmTitle] is null).
 * @param content The foreground content to display.
 */
@Composable
fun AppSwipeToDeleteContainer(
    modifier: Modifier = Modifier,
    shape: Shape = AppTheme.shapes.small,
    confirmTitle: String? = null,
    confirmButton: String = "Delete",
    cancelButton: String = "Cancel",
    onDelete: () -> Unit,
    content: @Composable RowScope.() -> Unit,
) {
    var showConfirmDialog by rememberSaveable { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val dismissState = rememberSwipeToDismissBoxState()

    LaunchedEffect(dismissState) {
        snapshotFlow { dismissState.currentValue }
            .collect { value ->
                if (value == SwipeToDismissBoxValue.EndToStart) {
                    if (confirmTitle != null) {
                        showConfirmDialog = true
                    } else {
                        onDelete()
                        dismissState.reset()
                    }
                }
            }
    }

    if (showConfirmDialog) {
        AppAlertDialog(
            title = confirmTitle,
            positiveButton = confirmButton,
            negativeButton = cancelButton,
            positiveButtonType = AlertButtonType.DESTRUCTIVE,
            negativeButtonType = AlertButtonType.CANCEL,
            onPositiveClick = {
                showConfirmDialog = false
                onDelete()
            },
            onNegativeClick = {
                showConfirmDialog = false
                scope.launch { dismissState.reset() }
            },
            onDismissRequest = {
                showConfirmDialog = false
                scope.launch { dismissState.reset() }
            },
        )
    }

    SwipeToDismissBox(
        modifier = modifier,
        state = dismissState,
        enableDismissFromStartToEnd = false,
        backgroundContent = {
            val offset = runCatching { dismissState.requireOffset() }.getOrDefault(0f)
            val alpha = ((-offset) / 200f).coerceIn(0f, 1f)
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .clip(shape)
                    .background(DeleteRed.copy(alpha = alpha)),
                contentAlignment = Alignment.CenterEnd,
            ) {
                Icon(
                    modifier = Modifier
                        .padding(end = 20.dp)
                        .size(24.dp),
                    imageVector = Icons.Default.Delete,
                    contentDescription = null,
                    tint = Color.White.copy(alpha = alpha),
                )
            }
        },
        content = content,
    )
}
